import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Clergy, ParishComplete } from './parish-model';
import { FormButtons } from '../form-element/form-buttons';
import { Wysiwyg } from '../wysiwyg';
import { useEpiParishSettings, useEpiParishSettingsLoaded } from '../../hooks/useEpiParishSettings';
import { MailDestination } from '../mail/mail-model';
import { EcdRoutes } from '../../constants/routes';
import { getBasePath } from '../../helpers/routing';
import { EcdStates } from '../../../root/ecd-model';
import { MapItAction } from './mapIt-action';

interface Props {
    selectedParish: ParishComplete;
    onNewSearch: () => void;
    onNavigateBack: () => void;
    onSelectClergyById: (clergyId: string | number) => void;
    selectParishById: (parishId: string | number, isReplace: boolean) => void;
    onNavigateToSendEmail: (parishId: string | number, destination: MailDestination) => void;
}

function ParishDetail(props: Props) {
    const history = useHistory();
    const { parishid } = useParams<any>();

    const { selectedParish, onNewSearch, onNavigateBack,
        onSelectClergyById, selectParishById, onNavigateToSendEmail } = props;
    const parishSettingsLoaded = useEpiParishSettingsLoaded();
    const parishSettings = useEpiParishSettings();

    const currentlanguage = useSelector<EcdStates, string>(state => state?.globalization?.currentlanguage);    
    const basePath = getBasePath(currentlanguage);

    useEffect(() => {
		if (parishid && !selectedParish?.id){
            selectParishById(parishid, true);
        }
	}, []);

    if (!parishid && !selectedParish?.id){
        history.push(`${basePath}/${EcdRoutes.Clergy}`);        
    }
    
    if (!parishSettingsLoaded || !parishSettings) {
        return <LoadingCircle />;
    }

    function NavigationButtons() {
        return (<>
            <FormButtons
                disabled={false}
                submitText={parishSettings?.newSearchButton || 'New Search'}
                cancelText={parishSettings?.backButton}
                onSubmit={onNewSearch}
                onCancel={onNavigateBack}
                submitDisabled={false}
                hideCancelButton={!parishSettings?.backButton}
            />
        </>);
    }

    function SimpleGroupView(title: string, emptyMessage: string, content: string) {
        return (<div className='quickfind-detail-section'>
            <div className='quickfind-detail-section-title'><p><strong>{title}</strong></p></div>
            <div className='quickfind-detail-section-inner'>{content || <Wysiwyg content={emptyMessage} />}</div>
        </div>);
    }

    function EmailAction() {
        if (selectedParish?.email) {
            return (<>
                <div> <button className='quickfind-detail-section-button-link' type='button'
                    onClick={() => onNavigateToSendEmail(
                        selectedParish.id,
                        {
                        email: selectedParish.email,
                        name: selectedParish.name,
                        itemGuid: selectedParish.id,
                    })} > {parishSettings.sendEmailLabel} </button></div>
            </>);
        }

        return (<>
            <div><Wysiwyg content={parishSettings.emailEmptyMessage} /></div>
        </>);
    }

    function WebsiteAction() {
        if (selectedParish?.website?.length > 7) {
            return (<>
                <div> <a href={selectedParish.website} target='_blank' rel="noreferrer" > {selectedParish.website} </a></div>
            </>);
        }

        return (<>
            <div><Wysiwyg content={parishSettings.websiteEmptyMessage} /></div>
        </>);
    }

    function WebsiteEmail() {
        return (<>
            <div className='quickfind-detail-section-title'><p><strong>{parishSettings.websiteEmailTitle}</strong></p></div>
            <div className='quickfind-detail-section-inner'>
                <WebsiteAction />
                <EmailAction />
            </div>
        </>);
    }

    function RenderAddress() {
        if (!selectedParish?.address?.address1) {
            return (<>
                <div>{parishSettings.addressTitle}</div>
                <div> <Wysiwyg content={parishSettings.addressEmptyMessage} /></div>
            </>);
        }

        return (<div className='quickfind-detail-section'>
            <div className='quickfind-detail-section-inner'>
                <div className='quickfind-detail-section-title'><p><strong>{parishSettings.addressTitle}</strong></p></div>
                <div>{selectedParish?.address?.address1 || ''}</div>
                <div>{selectedParish?.address?.address2 || ''}</div>
                <div>{selectedParish?.address?.address3 || ''}</div>
                <div>{`${selectedParish?.address?.city ? `${selectedParish?.address?.city},` : ''} ${selectedParish?.address?.state || ''} ${selectedParish?.address?.postal_code || ''}`}</div>
                <div>{selectedParish?.address?.country || ''}</div>
            </div>
        </div>);
    }

    function RecordType() {
        return SimpleGroupView(parishSettings.recordTypeTitle, parishSettings.recordTypeEmptyMessage, selectedParish?.type);
    }

    function Diocese() {
        return SimpleGroupView(parishSettings.dioceseTitle, parishSettings.dioceseEmptyMessage, selectedParish?.diocese);
    }

    function Phone() {
        return SimpleGroupView(parishSettings.phoneTitle, parishSettings.phoneEmptyMessage, selectedParish?.number);
    }

    function Fax() {
        return SimpleGroupView(parishSettings.faxTitle, parishSettings.faxEmptyMessage, selectedParish?.fax);
    }

    function ClergyView(clergyItem: Clergy) {
        if (!clergyItem) return null;

        const parsedName = `${clergyItem.suffix ? clergyItem.suffix : ''}${clergyItem.first_name}${clergyItem.middle_name ? ` ${clergyItem.middle_name} ` : ' '}${clergyItem.last_name}`.trim();

        if (clergyItem.id) {
            return (<>
                <div key={clergyItem.id}>
                    <div> <button className='quickfind-detail-section-button-link' type='button' onClick={() => { onSelectClergyById(clergyItem.id); }} > {parsedName} </button></div>
                </div>
            </>);
        }

        return (<>
            <div key={clergyItem.id}>
                <div>{parsedName}</div>
            </div>
        </>);
    }

    function ParishClergy() {
        if (selectedParish?.clergy?.length > 0) {
            return (<div className='quickfind-detail-section'>
                <div className='quickfind-detail-section-title'><p><strong>{parishSettings.parishClergyTitle}</strong></p></div>
                <div className='quickfind-detail-section-inner'>{selectedParish.clergy.map(clergyItem => ClergyView(clergyItem))}</div>
            </div>);
        }

        return null;
    }

    if (!selectedParish) return null;

    return (<>
        <h3 className='ecd-page-content-title'>
                {parishSettings.pageTitle}
        </h3>
        <div className='ecd-page-content-section'>
            <div className='ecd-page-content-subtitle-container'>
                <h3>
                    {selectedParish.name}
                </h3>
                { selectedParish?.maps_link &&
                    <div className='ecd-page-content-subtitle-map'>
                        <div className='ecd-page-content-subtitle-icon'></div>
                        <MapItAction mapsLink={selectedParish?.maps_link} mapItTitle={parishSettings?.mapItTitle} />                    
                    </div>}
            </div>
            <div className='quickfind-detail-section-columns'>
                <div>
                    <RenderAddress />
                    <WebsiteEmail />
                </div>
                <div>
                    <RecordType />
                    <Diocese />
                    <Phone />
                    <Fax />

                    <ParishClergy />
                </div>
            </div>
            <NavigationButtons />
        </div>
        </>
    );
}

export default ParishDetail;
